"use client";

import { useState, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/design-system/button";
import type { SessionRecovery } from "./sessionRecovery";

export function SessionRecoveryBanner({
  recovery,
}: {
  recovery: SessionRecovery;
}) {
  const failedSession = useSyncExternalStore(
    recovery.subscribe,
    recovery.getSnapshot,
    () => null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  if (failedSession === null) return null;

  async function signIn() {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      const result = await recovery.recover(
        `${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      if (result === "changed") {
        // Server-rendered user/org context must refresh with the new cookie.
        window.location.reload();
      } else {
        window.location.assign("/discovery");
      }
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Sign-in required"
      role="alert"
      className="fixed bottom-4 left-4 right-4 z-[100] mx-auto flex max-w-3xl flex-wrap items-center gap-nf-ds-4 rounded-nf-ds-lg border border-nf-ds-warning bg-nf-ds-tone-warn-bg p-nf-ds-4 font-nf-ds-sans text-nf-ds-tone-warn-fg shadow-nf-ds-lg"
    >
      <div className="min-w-0 flex-1">
        <p className="font-[600]">Your session has ended</p>
        <p className="text-sm">
          Sign in again to load and save your work. Copy any unsaved work before
          continuing.
        </p>
        {error && (
          <p className="mt-2 text-sm">
            Couldn’t restart sign-in. Your page is still open. Please try again.
          </p>
        )}
      </div>
      <Button variant="ink" onClick={signIn} disabled={busy}>
        {busy ? "Opening sign-in…" : "Sign in again"}
      </Button>
    </section>
  );
}
